<template>
  <div class="scan-page">
    <div class="scan-shell">
      <div class="scan-header">
        <div>
          <h1>快递二维码识别</h1>
          <p>拍照或从相册选择图片，自动识别快递二维码或条形码并提取运单号</p>
        </div>
      </div>

      <div class="scan-card">
        <div class="upload-box" @click="triggerFileInput">
          <input
            ref="fileInput"
            class="file-input"
            type="file"
            accept="image/*"
            capture="environment"
            @change="handleFileChange"
          />
          <div v-if="previewUrl" class="preview-wrapper">
            <img :src="previewUrl" alt="预览图" class="preview-image" />
          </div>
          <div v-else class="upload-placeholder">
            <div class="upload-icon">📷</div>
            <p>点击拍照或选择相册图片</p>
          </div>
        </div>

        <div class="action-row">
          <button type="button" class="secondary-btn" @click="triggerFileInput">重新选择</button>
          <button type="button" class="secondary-btn" @click="recognizeCurrentImage" :disabled="!selectedFile || isDecoding">
            {{ isDecoding ? '识别中...' : '重新识别' }}
          </button>
        </div>

        <div class="form-section">
          <label>运单号</label>
          <input v-model.trim="trackingNumber" type="text" placeholder="识别后自动提取，也可手动填写" />
        </div>

        <button class="submit-btn" type="button" @click="submitUpload" :disabled="isUploading || !selectedFile || !trackingNumber">
          {{ isUploading ? '上传中...' : '上传保存' }}
        </button>

        <div v-if="statusMessage" class="status-message" :class="statusType">
          {{ statusMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import API_CONFIG from '@/api/config'

// ========== 阿里通义千问 API_KEY（内部使用，切勿对外发布） ==========
const DASHSCOPE_API_KEY = 'sk-194155f5f5a14c1a9f5a144ce6edb478'
const API_URL = 'https://dashscope.aliyuncs.com/api/v1/services/aigc/multimodal-generation/generation'

export default {
  name: 'ExpressScan',
  data() {
    return {
      selectedFile: null,
      previewUrl: '',
      trackingNumber: '',
      isDecoding: false,
      isUploading: false,
      statusMessage: '',
      statusType: ''
    }
  },
  methods: {
    triggerFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click()
      }
    },
    async handleFileChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) {
        return
      }
      if (!file.type || !file.type.startsWith('image/')) {
        this.statusType = 'error'
        this.statusMessage = '请选择图片文件'
        return
      }
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
      }
      this.selectedFile = file
      this.previewUrl = URL.createObjectURL(file)
      this.trackingNumber = ''
      await this.recognizeCurrentImage()
    },
    async recognizeCurrentImage() {
      if (!this.selectedFile) {
        return
      }
      this.isDecoding = true
      this.statusMessage = ''
      this.statusType = ''
      try {
        const trackingNumber = await this.recognizeByQwenVL(this.selectedFile)
        this.trackingNumber = trackingNumber
        if (trackingNumber) {
          this.statusType = 'success'
          this.statusMessage = '识别成功，已自动提取运单号'
        } else {
          this.statusType = 'warning'
          this.statusMessage = '未自动识别到运单号，请您直接手动填写'
        }
      } catch (error) {
        console.error('识别错误:', error)
        this.statusType = 'error'
        this.statusMessage = '识别失败，请重新尝试或直接手动填写运单号'
      } finally {
        this.isDecoding = false
      }
    },
    // 图片压缩到1M以内
    compressImage(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = (e) => {
          const img = new Image()
          img.src = e.target.result
          img.onload = () => {
            let quality = 0.8
            let width = img.width
            let height = img.height
            
            // 如果图片尺寸太大，先缩小尺寸
            const maxWidth = 1920
            const maxHeight = 1920
            if (width > maxWidth || height > maxHeight) {
              const ratio = Math.min(maxWidth / width, maxHeight / height)
              width = Math.floor(width * ratio)
              height = Math.floor(height * ratio)
            }
            
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0, width, height)
            
            // 循环调整 quality 直到文件大小小于1M
            const compress = (q) => {
              const result = canvas.toDataURL('image/jpeg', q)
              const size = (result.length - 'data:image/jpeg;base64,'.length) * 0.75
              if (size <= 1024 * 1024 || q <= 0.1) {
                resolve(result.split(',')[1])
              } else {
                compress(q - 0.1)
              }
            }
            compress(quality)
          }
          img.onerror = reject
        }
        reader.onerror = reject
      })
    },
    // 将图片转为 Base64（纯 base64 部分，不包含 data: 前缀）
    async fileToBase64(file) {
      // 先压缩图片
      return await this.compressImage(file)
    },
    // 调用阿里通义千问 qwen-vl-plus 多模态接口识别
    async recognizeByQwenVL(file) {
      const base64Img = await this.fileToBase64(file)
      
      const requestBody = {
        model: "qwen-vl-plus",
        input: {
          messages: [
            {
              role: "user",
              content: [
                {
                  image: `data:image/jpeg;base64,${base64Img}`
                },
                {
                  text: "请从这张快递面单图片中，仔细识别并提取运单号，只输出纯运单号，不要其他任何解释、不要地址电话。识别不到就返回空字符串"
                }
              ]
            }
          ]
        },
        parameters: {}
      }

      const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${DASHSCOPE_API_KEY}`
        },
        body: JSON.stringify(requestBody)
      })

      const data = await res.json()
      if (data.code) {
        console.error('阿里API请求失败:', data.message)
        return ''
      }
      
      if (data.output && data.output.choices && data.output.choices.length > 0) {
        const content = data.output.choices[0].message.content[0].text || ''
        // 简单清洗，只保留字母、数字和常见的运单符号
        return content.trim().replace(/[^\w\-\s]/g, '').replace(/\s+/g, '')
      }
      return ''
    },
    async submitUpload() {
      if (!this.selectedFile) {
        this.statusType = 'error'
        this.statusMessage = '请先选择图片'
        return
      }
      if (!this.trackingNumber) {
        this.statusType = 'error'
        this.statusMessage = '请填写运单号'
        return
      }

      this.isUploading = true
      this.statusMessage = ''
      this.statusType = ''
      try {
        const formData = new FormData()
        formData.append('file', this.selectedFile)
        formData.append('trackingNumber', this.trackingNumber)
        const response = await axios.post(API_CONFIG.expressUpload(), formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        if (response.data && response.data.code === 200) {
          this.statusType = 'success'
          this.statusMessage = '上传成功，记录已保存'
          this.selectedFile = null
          this.trackingNumber = ''
          if (this.previewUrl) {
            URL.revokeObjectURL(this.previewUrl)
            this.previewUrl = ''
          }
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = ''
          }
          return
        }
        this.statusType = 'error'
        this.statusMessage = response.data?.msg || '上传失败'
      } catch (error) {
        this.statusType = 'error'
        this.statusMessage = error.response?.data?.msg || '上传失败，请稍后重试'
      } finally {
        this.isUploading = false
      }
    }
  },
  beforeDestroy() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl)
    }
  }
}
</script>

<style scoped>
.scan-page {
  min-height: 100vh;
  height: 100vh;
  background: linear-gradient(180deg, #eef6ff 0%, #f8fbff 100%);
  padding: 12px 12px 20px;
  box-sizing: border-box;
  overflow-y: auto;
}

.scan-shell {
  max-width: 540px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.scan-header {
  margin-bottom: 10px;
  flex-shrink: 0;
}

.scan-header h1 {
  margin: 0 0 4px;
  font-size: 22px;
  color: #183153;
}

.scan-header p {
  margin: 0;
  color: #5f6f85;
  line-height: 1.4;
  font-size: 13px;
}

.scan-card {
  background: #fff;
  border-radius: 18px;
  padding: 14px;
  box-shadow: 0 12px 30px rgba(33, 94, 216, 0.08);
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.upload-box {
  border: 2px dashed #b7cdfd;
  border-radius: 16px;
  min-height: 180px;
  max-height: 40vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7fbff;
  overflow: hidden;
  flex-shrink: 0;
}

.file-input {
  display: none;
}

.upload-placeholder {
  text-align: center;
  color: #6b7a90;
}

.upload-icon {
  font-size: 36px;
  margin-bottom: 8px;
}

.preview-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}

.action-row {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-shrink: 0;
}

.secondary-btn,
.submit-btn {
  border: none;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.secondary-btn {
  flex: 1;
  background: #eef4ff;
  color: #215ed8;
}

.secondary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-section {
  margin-top: 10px;
  flex-shrink: 0;
}

.form-section label {
  display: block;
  margin-bottom: 6px;
  color: #23354d;
  font-size: 13px;
  font-weight: 600;
}

.form-section input,
.form-section textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d7e3fb;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s ease;
}

.form-section input:focus,
.form-section textarea:focus {
  border-color: #4b84ff;
  box-shadow: 0 0 0 3px rgba(75, 132, 255, 0.12);
}

.submit-btn {
  width: 100%;
  margin-top: 12px;
  background: linear-gradient(135deg, #215ed8, #4b84ff);
  color: white;
  flex-shrink: 0;
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.status-message {
  margin-top: 10px;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  flex-shrink: 0;
}

.status-message.success {
  background: #ebfff2;
  color: #177245;
  border: 1px solid #b8ebcd;
}
.status-message.warning {
  background: #fff7e6;
  color: #936200;
  border: 1px solid #ffd591;
}
.status-message.error {
  background: #fff1f1;
  color: #b73a3a;
  border: 1px solid #f2c0c0;
}
</style>
